/**
 * Allys View Switcher
 * 
 * Enhanced view mode toggle with Allys theme styling
 * Features modern design with gradients and smooth animations
 */

'use client';

import { motion } from 'framer-motion';

export type ViewMode = 'grid' | 'list' | 'map';

export interface AllysViewSwitcherProps {
  /**
   * Current active view mode
   */
  activeView: ViewMode;
  /**
   * Callback when view changes
   */
  onViewChange: (view: ViewMode) => void;
  /**
   * Labels for each view
   */
  labels?: {
    grid?: string;
    list?: string;
    map?: string;
  };
  /**
   * Custom className
   */
  className?: string;
}

/**
 * AllysViewSwitcher - Beautiful view mode toggle with Allys styling
 */
export function AllysViewSwitcher({
  activeView,
  onViewChange,
  labels = {
    grid: 'Grid',
    list: 'List',
    map: 'Map',
  },
  className = '',
}: AllysViewSwitcherProps) {
  const views: { mode: ViewMode; icon: React.ReactNode; label: string }[] = [
    {
      mode: 'grid',
      label: labels.grid || 'Grid',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
    },
    {
      mode: 'list',
      label: labels.list || 'List',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      ),
    },
    {
      mode: 'map',
      label: labels.map || 'Map',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
    },
  ];

  return (
    <div className={`inline-flex items-center bg-linear-to-r from-gray-50 to-gray-100 rounded-xl p-1.5 shadow-sm border border-gray-200 ${className}`}>
      {views.map((view) => {
        const isActive = activeView === view.mode;
        
        return (
          <motion.button
            key={view.mode}
            onClick={() => onViewChange(view.mode)}
            className={`
              relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold
              transition-all duration-300 font-inter
              ${isActive 
                ? 'text-white' 
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
            aria-label={view.label}
            aria-pressed={isActive}
            whileHover={!isActive ? { scale: 1.05 } : {}}
            whileTap={{ scale: 0.95 }}
          >
            {/* Active background with gradient */}
            {isActive && (
              <motion.div
                layoutId="activeViewBg"
                className="absolute inset-0 bg-linear-to-br from-[#243462] to-[#121C3B] rounded-lg shadow-md"
                initial={false}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                }}
              />
            )}
            
            {/* Gold glow for active state */}
            {isActive && (
              <motion.div
                className="absolute inset-0 rounded-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0.5, 0.8, 0.5] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                style={{
                  boxShadow: '0 0 20px rgba(187, 163, 57, 0.3)',
                }}
              />
            )}
            
            {/* Icon */}
            <motion.span 
              className="relative z-10"
              animate={isActive ? { scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 0.3 }}
            >
              {view.icon}
            </motion.span>
            
            {/* Label (hidden on mobile) */}
            <span className="relative z-10 hidden sm:inline">{view.label}</span>
            
            {/* Hover effect for inactive buttons */}
            {!isActive && (
              <motion.div
                className="absolute inset-0 bg-white/50 rounded-lg opacity-0 group-hover:opacity-100"
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              />
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

